// ** React Imports
import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

// ** Store & Actions
import { useSelector, useDispatch } from 'react-redux'
import { handleMenuCollapsed, handleContentWidth, handleMenuHidden } from '@store/actions/layout'

// ** Third Party Components
import classnames from 'classnames'
import { IconArrowUp } from '@tabler/icons-react'
import ScrollToTop from 'react-scroll-up'
import { Navbar, Card, CardTitle, CardText } from 'reactstrap'
import { Button } from 'dsm-react-lib'

// ** Configs
import themeConfig from '@configs/themeConfig'

// ** Custom Components
import Customizer from '@components/customizer'
import FooterComponent from './components/footer'
import NavbarComponent from './components/navbar'
import SidebarComponent from './components/menu/vertical-menu'
// ** Custom Hooks
import { useRTL } from '@hooks/useRTL'
import { useSkin } from '@hooks/useSkin'
import { useNavbarType } from '@hooks/useNavbarType'
import { useFooterType } from '@hooks/useFooterType'
import { useNavbarColor } from '@hooks/useNavbarColor'

// ** Styles
import '@styles/base/core/menu/menu-types/vertical-menu.scss'
import '@styles/base/core/menu/menu-types/vertical-overlay-menu.scss'

import Chatbot from '../../views/_pages/Chatbot/Chatbot'
import { checkIsDrawerMode } from '../../views/_pages/Chatbot/utils/helpers'
import { setIsChatOpen } from '../../_redux/actions/dataMarketPlaceActions'
import { useMediaQuery } from '@react-hook/media-query'

const VerticalLayout = (props) => {
  // ** Props
  const { children, navbar, footer, menu, routerProps, currentActiveItem } = props

  // ** Hooks
  const [skin, setSkin] = useSkin()
  const [isRtl, setIsRtl] = useRTL()
  const [navbarType, setNavbarType] = useNavbarType()
  const [footerType, setFooterType] = useFooterType()
  const [navbarColor, setNavbarColor] = useNavbarColor()
  const smallScreen = useMediaQuery('(max-width: 1800px)')

  // ** States
  const [isMounted, setIsMounted] = useState(false)
  const [menuVisibility, setMenuVisibility] = useState(false)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [currentPanelWidth, setCurrentPanelWidth] = useState(window.innerWidth * 0.3)

  // ** Store Vars
  const dispatch = useDispatch()
  const layoutStore = useSelector((state) => state.layoutReducer)

  // ** Update Window Width
  const handleWindowWidth = () => {
    setWindowWidth(window.innerWidth)
  }

  // ** Vars
  const location = useLocation()
  const { isChatOpen } = useSelector((state) => state.dataMarketPlaceReducer)
  const contentWidth = layoutStore.contentWidth
  const menuCollapsed = layoutStore.menuCollapsed
  const isHidden = layoutStore.menuHidden
  const drawerWidthRef = useRef(window.innerWidth * 0.35) // Default 35vw
  const drawerRef = useRef(null)
  const resizerRef = useRef(null)

  // Check if current route should show chatbot
  const isDrawerMode = checkIsDrawerMode(location.pathname)

  // ** Toggles Menu Collapsed
  const setMenuCollapsed = (val) => dispatch(handleMenuCollapsed(val))

  // ** Handles Content Width
  const setContentWidth = (val) => dispatch(handleContentWidth(val))

  // ** Handles Content Width
  const setIsHidden = (val) => dispatch(handleMenuHidden(val))

  const handleChatToggle = () => {
    dispatch(setIsChatOpen(!isChatOpen))
  }

  //** This function will detect the Route Change and will hide the menu on menu item click
  useEffect(() => {
    if (menuVisibility && windowWidth < 1200) {
      setMenuVisibility(false)
    }
  }, [location])

  //** Sets Window Size & Layout Props
  useEffect(() => {
    if (window !== undefined) {
      window.addEventListener('resize', handleWindowWidth)
    }
  }, [windowWidth])

  //** ComponentDidMount
  useEffect(() => {
    setIsMounted(true)
    return () => setIsMounted(false)
  }, [])

  //** Handle menu collapse on small screens
  useEffect(() => {
    if (smallScreen && isChatOpen && isDrawerMode) {
      dispatch(handleMenuCollapsed(true))
    }
  }, [smallScreen, isChatOpen])

  const handleMouseDown = (e) => {
    const startX = e.clientX
    const startWidth = drawerWidthRef.current

    // Prevent text selection while dragging
    document.body.style.userSelect = 'none'

    const handleMouseMove = (event) => {
      const newWidth = startWidth - (event.clientX - startX)
      const minWidth = window.innerWidth * 0.3 // 30vw
      const maxWidth = window.innerWidth * 0.5 // 50vw

      drawerWidthRef.current = Math.max(minWidth, Math.min(newWidth, maxWidth))

      // Update state to trigger re-render of styled components
      setCurrentPanelWidth(drawerWidthRef.current)

      if (drawerRef.current) {
        drawerRef.current.style.width = `${drawerWidthRef.current}px`
      }

      // Highlight resizer while moving
      if (resizerRef.current) {
        resizerRef.current.style.background = 'var(--color-primary)'
      }
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)

      // Allow text selection again
      document.body.style.userSelect = 'auto'

      // Remove highlight
      if (resizerRef.current) {
        resizerRef.current.style.background = 'transparent'
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // ** Vars
  const footerClasses = {
    static: 'footer-static',
    sticky: 'footer-fixed',
    hidden: 'footer-hidden'
  }

  const navbarWrapperClasses = {
    floating: 'navbar-floating',
    sticky: 'navbar-sticky',
    static: 'navbar-static',
    hidden: 'navbar-hidden'
  }

  const navbarClasses = {
    floating: 'floating-nav',
    sticky: 'fixed-top',
    static: 'navbar-static-top',
    hidden: 'd-none'
  }

  const bgColorCondition = navbarColor !== '' && navbarColor !== 'light' && navbarColor !== 'white'

  // Derived layout helpers (replace styled-component props)
  const isChatDrawerVisible = isChatOpen && isDrawerMode
  const sidebarWidth = menuCollapsed ? '81px' : '260px'
  const isDesktop = windowWidth >= 1200
  const resolvedNavWrapperClass = navbarWrapperClasses[navbarType] || 'navbar-floating'
  const resolvedNavbarClass = navbarClasses[navbarType] || 'floating-nav'

  const navWrapperWidth = isDesktop
    ? isChatDrawerVisible
      ? `calc(100% - ${currentPanelWidth}px - ${sidebarWidth})`
      : `calc(100% - ${sidebarWidth})`
    : '100%'

  const navWrapperLeft =
    isDesktop && isChatDrawerVisible && resolvedNavWrapperClass === 'navbar-floating'
      ? sidebarWidth
      : undefined

  const floatingNavStyle =
    resolvedNavbarClass === 'floating-nav' && isChatDrawerVisible && isDesktop
      ? {
          left: sidebarWidth,
          width: `calc(100% - ${currentPanelWidth}px - 4rem * 2 - ${sidebarWidth})`,
          margin: '1.3rem 2rem 0'
        }
      : undefined

  if (!isMounted) {
    return null
  }
  return (
    <div
      className={classnames(
        `wrapper vertical-layout ${navbarWrapperClasses[navbarType] || 'navbar-floating'} ${
          footerClasses[footerType] || 'footer-static'
        }`,
        {
          // Modern Menu
          'vertical-menu-modern': windowWidth >= 1200,
          'menu-collapsed': menuCollapsed && windowWidth >= 1200,
          'menu-expanded': !menuCollapsed && windowWidth > 1200,

          // Overlay Menu
          'vertical-overlay-menu': windowWidth < 1200,
          'menu-hide': !menuVisibility && windowWidth < 1200,
          'menu-open': menuVisibility && windowWidth < 1200
        }
      )}
      {...(isHidden ? { 'data-col': '1-column' } : {})}>
      {!isHidden ? (
        <SidebarComponent
          skin={skin}
          menu={menu}
          menuCollapsed={menuCollapsed}
          menuVisibility={menuVisibility}
          setMenuCollapsed={setMenuCollapsed}
          setMenuVisibility={setMenuVisibility}
          routerProps={routerProps}
          currentActiveItem={currentActiveItem}
        />
      ) : null}

      <div
        id="topbar"
        className={`fixed right-0 top-0 z-[11] h-[100px] ${resolvedNavWrapperClass}`}
        style={{ width: navWrapperWidth, ...(navWrapperLeft ? { left: navWrapperLeft } : {}) }}>
        <Navbar
          expand="lg"
          light={skin !== 'dark'}
          dark={skin === 'dark' || bgColorCondition}
          color={bgColorCondition ? navbarColor : undefined}
          style={floatingNavStyle}
          className={classnames(
            `header-navbar navbar align-items-center ${resolvedNavbarClass} navbar-shadow`
          )}>
          <div className="navbar-container d-flex search-tour">
            {navbar ? (
              navbar({ setMenuVisibility, skin, setSkin })
            ) : (
              <NavbarComponent
                setMenuVisibility={setMenuVisibility}
                skin={skin}
                setSkin={setSkin}
              />
            )}
          </div>
        </Navbar>
      </div>
      <div className="layout-container flex h-[calc(100%-48px)]">
        <div
          className="relative z-0 flex h-full grow flex-col overflow-x-hidden overflow-y-auto transition-[width] duration-[400ms] ease-in-out"
          style={{ width: isChatDrawerVisible ? `calc(100% - ${currentPanelWidth}px)` : '100%' }}>
          {children}
          {/* Vertical Nav Menu Overlay */}
          <div
            className={classnames({
              show: menuVisibility
            })}
            onClick={() => setMenuVisibility(false)}></div>
          {/* Vertical Nav Menu Overlay */}
          {themeConfig.layout.customizer === true ? (
            <Customizer
              skin={skin}
              setSkin={setSkin}
              footerType={footerType}
              setFooterType={setFooterType}
              navbarType={navbarType}
              setNavbarType={setNavbarType}
              navbarColor={navbarColor}
              setNavbarColor={setNavbarColor}
              isRtl={isRtl}
              setIsRtl={setIsRtl}
              layout={props.layout}
              setLayout={props.setLayout}
              isHidden={isHidden}
              setIsHidden={setIsHidden}
              contentWidth={contentWidth}
              setContentWidth={setContentWidth}
              menuCollapsed={menuCollapsed}
              setMenuCollapsed={setMenuCollapsed}
              transition={props.transition}
              setTransition={props.setTransition}
              themeConfig={themeConfig}
            />
          ) : null}
          {/* {themeConfig.layout.scrollTop === true ? (
        <div className="scroll-to-top">
          <ScrollToTop showUnder={300} style={{ bottom: '5%' }}>
            <Button className="btn-icon" color="primary"
              icon={<IconArrowUp size={14} />}>
            </Button>
          </ScrollToTop>
        </div>
      ) : null} */}
        </div>

        {isChatDrawerVisible && (
          <div className="right-0 top-0 z-[1000] flex h-full overflow-hidden">
            <div
              className="z-[1000] left-0 w-[6px] h-full cursor-ew-resize hover:bg-[var(--resizer-hover-color)]"
              style={{ '--resizer-hover-color': 'var(--color-primary)' }}
              theme="var(--color-primary)"
              onMouseDown={handleMouseDown}
              ref={resizerRef}
              $panelWidth={`${drawerWidthRef.current}px`}
              $chatVisible={isChatOpen}
            />
            <div
              className={`flex-shrink-0 transition-[width] duration-[400ms] ease-in-out relative max-w-[50vw] ${
                isChatOpen ? 'min-w-[30vw]' : ''
              }`}
              // Dynamic width from drawerWidthRef.current (updated during resize) - cannot use Tailwind
              style={{
                width: isChatOpen ? `${drawerWidthRef.current}px` : '0'
              }}
              ref={drawerRef}>
              <Chatbot />
            </div>
          </div>
        )}
      </div>
      <footer
        className={classnames(`footer ${footerClasses[footerType] || 'footer-static'}`, {
          'd-none': footerType === 'hidden'
        })}>
        {footer ? (
          footer({ footerType, footerClasses })
        ) : (
          <FooterComponent footerType={footerType} footerClasses={footerClasses} />
        )}
      </footer>
    </div>
  )
}

export default VerticalLayout
